import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTRPC } from '../lib/trpc'
import {
  Button,
  Field,
  FieldSection,
  Notice,
  SettingBlock,
  TextInput,
} from './index'
import { useConfirm } from './useConfirm'

/**
 * A password, for people who would rather type one than fetch an inbox.
 *
 * Setting one adds a way in and takes none away: the link keeps working, which
 * is also why there is no reset here. Somebody who has forgotten it signs in
 * with a link and sets a new one, so recovery is the mechanism the app already
 * has rather than a second one to get right.
 */
export function Password() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { confirm, dialog } = useConfirm()
  const [next, setNext] = useState('')

  const has = useQuery(trpc.auth.hasPassword.queryOptions())
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.auth.hasPassword.queryKey(),
    })
  const set = useMutation(
    trpc.auth.setPassword.mutationOptions({
      onSuccess: () => {
        setNext('')
        invalidate()
      },
    }),
  )
  const clear = useMutation(
    trpc.auth.clearPassword.mutationOptions({ onSuccess: invalidate }),
  )

  return (
    <>
      <FieldSection
        title="Password"
        description={
          has.data?.set
            ? 'You can sign in with a password or with an emailed link. Forgotten it? Sign in with a link and set a new one here.'
            : 'You sign in with an emailed link. Set a password if you would rather type one — the link keeps working either way.'
        }
      >
        <Field
          label={has.data?.set ? 'New password' : 'A password'}
          /* Stated before it is typed rather than after it is refused: the rule
             is length alone, because a digit-and-symbol rule reliably produces
             the same few shapes and sends people to a sticky note. */
          info="At least 12 characters. Nothing else — no digit or symbol is required, and none is worth the sticky note it produces."
        >
          <TextInput
            type="password"
            value={next}
            autoComplete="new-password"
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <div className="account__act">
          <Button
            disabled={next.length < 12 || set.isPending}
            onClick={() => set.mutate({ password: next })}
          >
            {has.data?.set ? 'Replace password' : 'Set password'}
          </Button>
        </div>
        {set.error ? <Notice tone="error">{set.error.message}</Notice> : null}
        {set.isSuccess ? <Notice>Password set.</Notice> : null}
      </FieldSection>

      {has.data?.set ? (
        <SettingBlock
          title="Remove your password"
          description="You go back to signing in with an emailed link, as you did before. Nothing else changes and no session ends."
        >
          <Button
            variant="danger"
            onClick={async () => {
              const ok = await confirm({
                title: 'Remove your password?',
                body: 'You will sign in with an emailed link, as you did before. Nothing else changes and no session ends.',
                verb: 'Remove password',
              })
              if (ok) clear.mutate()
            }}
          >
            Remove password
          </Button>
        </SettingBlock>
      ) : null}
      {dialog}
    </>
  )
}
